import { Link } from "react-router-dom";
import { useState } from "react";
import { Target, Compass, Sparkles, X } from "lucide-react";


const BOARD = [
  {
    id: "nsikani",
    name: "Dr. Mlungele Nsikani",
    role: "Chairperson",
    term: "2024–2027",
    affiliation: "Senior Scientist, South African National Biodiversity Institute (SANBI)",
    photo: "/api/media/ser-africa/uploads/nsikani-bd0f97b6.jpg",
    tagline: "Ecological restoration scientist advancing biodiversity conservation across Africa.",
    bio: [
      "Dr. Mlungele Nsikani is an ecological restoration scientist and experienced leader with a strong commitment to advancing ecosystem restoration and biodiversity conservation across Africa. He currently serves as a Senior Scientist at the South African National Biodiversity Institute (SANBI) and as Chairperson of the Board of the Society for Ecological Restoration Africa Chapter.",
      "Through his scientific and leadership roles, Dr. Nsikani contributes to strengthening restoration practice, research, knowledge exchange and collaboration across the continent. His work emphasizes scientifically informed, locally relevant and socially inclusive approaches to restoring degraded ecosystems while supporting biodiversity, climate resilience and community wellbeing.",
      "As Chairperson of SER Africa, Dr. Nsikani is committed to building partnerships, strengthening restoration capacity and mobilising African-led action to advance the global restoration movement. His leadership reflects a vision for a connected African restoration community where science, policy, traditional knowledge and local action work together to deliver meaningful ecological and social outcomes.",
    ],
  },
  {
    id: "alele",
    name: "Dr. Peter Alele",
    role: "Vice-Chair",
    term: "2026–2027",
    affiliation: "Africa Regional Director, Forest Stewardship Council · Nairobi, Kenya",
    photo: "/api/media/ser-africa/uploads/alele-788afd8f.jpg",
    tagline: "20+ years leading ecological restoration and conservation across the continent.",
    bio: [
      "Dr. Peter Alele is a seasoned leader with more than 20 years of professional experience in ecological restoration and conservation. He is currently the Vice Chair of the Board of the SER Africa Chapter and was a member of the Advisory Committee that led to the establishment of the SER Africa Chapter.",
      "Dr. Alele is also the Africa Regional Director for the Forest Stewardship Council (FSC) and has served as a Member of the Strategic Advisory Committee for the Global Evergreening Alliance (GEA) since July 2018. He holds a PhD in Ecology, an MSc in Biology, and a BSc in Forestry.",
    ],
  },
  {
    id: "munro",
    name: "Lynette Munro",
    role: "Secretary",
    term: "",
    affiliation: "Conservation Partnership Facilitator, Nature Connect",
    photo: "/api/media/ser-africa/uploads/munro-0f83f62d.jpg",
    tagline: "Building conservation partnerships and innovative biodiversity financing.",
    bio: [
      "Lynette Munro is a conservation professional with training in botany and environmental management and a strong focus on building partnerships and innovative approaches to biodiversity conservation. She currently serves as a Conservation Partnership Facilitator at Nature Connect, where she works to identify stable, long-term financing opportunities that can expand employment through the stewardship of high-value biodiversity while contributing to the growth of the conservation estate.",
      "Her work explores collaborative management and co-funding mechanisms within the development sector, including the use of biodiversity offsets as a tool within environmental impact assessment processes. Through these approaches, Lynette is committed to strengthening partnerships between conservation, development and other sectors to create sustainable solutions for biodiversity protection.",
      "As Secretary of the Society for Ecological Restoration – Africa (SER Africa), Lynette supports the Chapter's governance, coordination and administration while contributing to its broader mission of strengthening the restoration community across Africa. Her experience in conservation partnerships, environmental management and innovative financing brings valuable perspectives to SER Africa's efforts to advance collaborative and sustainable ecosystem restoration across the continent.",
    ],
  },
  {
    id: "whitley",
    name: "Andrew Whitley",
    role: "Treasurer",
    term: "",
    affiliation: "Wildlands Conservation Trust · KwaZulu-Natal, South Africa",
    photo: "/api/media/ser-africa/uploads/whitley-7a9037a6.jpg",
    tagline: "Landscape-scale restoration practitioner turning knowledge into on-the-ground action.",
    bio: [
      "Andrew Whitley is an ecosystem restoration practitioner and environmental conservation professional with extensive experience in ecological restoration and landscape-scale conservation in South Africa. He is currently the Treasurer of the Society for Ecological Restoration – Africa (SER Africa) and manages ecosystem restoration initiatives at the Wildlands Conservation Trust, one of South Africa's leading environmental non-governmental organisations.",
      "Andrew began his conservation career managing invasive alien plant control programmes before expanding his work into a broader range of ecosystem restoration initiatives. His experience includes the propagation and large-scale planting of indigenous trees, habitat restoration, and practical interventions aimed at restoring degraded landscapes and strengthening ecological resilience.",
      "Through his work with Wildlands and SER Africa, Andrew is committed to advancing restoration practice across the continent and supporting greater collaboration among conservation practitioners, researchers, organisations and communities. He is particularly passionate about translating restoration knowledge into practical, scalable action that can contribute to healthier ecosystems and sustainable livelihoods across Africa.",
    ],
  },
];


export default function AboutPage() {
  const [openBio, setOpenBio] = useState(null);
  return (
    <div data-testid="about-page">
      <section
        className="ser-section text-center"
        style={{
          background:
            "linear-gradient(160deg, var(--ser-primary-dark) 0%, var(--ser-primary) 70%)",
          color: "white",
        }}
      >
        <div className="ser-container max-w-4xl">
          <p className="eyebrow mb-4" style={{ color: "var(--ser-clay)" }}>About Us</p>
          <h1 className="font-serif-display text-5xl md:text-7xl mb-6">About SER Africa</h1>
          <p className="text-lg" style={{ color: "rgba(255,255,255,0.8)" }}>
            A detailed look at who we are, what we stand for, and the impact we're
            making across the continent.
          </p>
        </div>
      </section>

      <section className="ser-section" data-testid="about-ser-global">
        <div className="ser-container max-w-4xl">
          <p className="eyebrow mb-3">Our Roots</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
            Our relationship with SER Global
          </h2>
          <p className="leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
            SER Africa operates as the official Africa Chapter of the Society for Ecological Restoration (SER),
            a global organisation with over four decades of leadership in restoration science and practice.
            As a regional chapter, we localise SER's global standards, tools, and research for the unique
            ecological and socio-economic contexts of the African continent.
          </p>
          <p className="leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
            This relationship gives our members access to an international network of restoration
            professionals, the SER journal <em>Restoration Ecology</em>, and the SER Connect platform for
            collaboration and knowledge sharing.
          </p>
        </div>
      </section>

      <section className="ser-section" style={{ backgroundColor: "var(--ser-sand)" }} data-testid="about-vmg">
        <div className="ser-container max-w-5xl">
          <p className="eyebrow mb-3">What Guides Us</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-12" style={{ color: "var(--ser-primary-dark)" }}>
            Vision, Mission &amp; Goals
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Compass, title: "Vision", text: "A future where Africa's degraded ecosystems are restored and sustainably managed — supporting biodiversity, livelihoods, and climate resilience." },
              { icon: Target, title: "Mission", text: "To advance the science, practice, and policy of ecological restoration in Africa through collaboration, capacity building, and knowledge exchange." },
              { icon: Sparkles, title: "Goals", text: "Grow a pan-African restoration network, promote evidence-based practice, develop the next generation of practitioners, and influence policy at continental level." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="ser-card p-8" data-testid={`vmg-${title.toLowerCase()}`}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: "var(--ser-primary)", color: "white" }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif-display text-2xl mb-3" style={{ color: "var(--ser-primary-dark)" }}>
                  {title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-section-alt)" }}
        data-testid="about-strategic-plan"
      >
        <div className="ser-container max-w-6xl grid md:grid-cols-2 gap-10 items-center">
          <a
            href="https://youtu.be/F4b3xygkamI"
            target="_blank"
            rel="noopener noreferrer"
            className="ser-card group block overflow-hidden"
            data-testid="strategic-plan-video"
          >
            <div className="aspect-video relative bg-black">
              <img
                src="https://img.youtube.com/vi/F4b3xygkamI/maxresdefault.jpg"
                onError={(ev) => { ev.currentTarget.src = "https://img.youtube.com/vi/F4b3xygkamI/hqdefault.jpg"; }}
                alt="Launch of the SER Africa Strategic Plan 2025–2030"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: "rgba(255,255,255,0.94)" }}
                  aria-hidden
                >
                  <span
                    style={{
                      display: "inline-block", width: 0, height: 0,
                      borderTop: "10px solid transparent", borderBottom: "10px solid transparent",
                      borderLeft: "16px solid var(--ser-primary-dark)", marginLeft: "4px",
                    }}
                  />
                </span>
              </div>
            </div>
          </a>
          <div>
            <p className="eyebrow mb-3">Watch the launch</p>
            <h2
              className="font-serif-display text-2xl md:text-3xl uppercase mb-4"
              style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
            >
              Launch of the SER Africa Strategic Plan 2025–2030
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
              A formal launch event welcoming participants and outlining the vision, goals and
              priorities for ecological restoration across Africa over the next five years.
            </p>
            <ul className="text-sm space-y-2 mb-6" style={{ color: "var(--ser-text-muted)" }}>
              <li><strong style={{ color: "var(--ser-teal)" }}>Strategic Plan Overview</strong> — the 2025–2030 plan and SER Africa's mission to strengthen restoration efforts across the continent.</li>
              <li><strong style={{ color: "var(--ser-teal)" }}>Collaboration Focus</strong> — partnerships with communities, governments and international stakeholders.</li>
              <li><strong style={{ color: "var(--ser-teal)" }}>Goals &amp; Priorities</strong> — biodiversity conservation, wetlands protection and capacity building.</li>
              <li><strong style={{ color: "var(--ser-teal)" }}>Engagement</strong> — a webinar format that positions ecological restoration as central to sustainable development in Africa.</li>
            </ul>
            <a
              href="https://youtu.be/F4b3xygkamI"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ser-primary"
              data-testid="strategic-plan-watch-btn"
            >
              Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      <section className="ser-section" data-testid="about-leadership">
        <div className="ser-container max-w-6xl">
          <p className="eyebrow mb-3">Leadership</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
            Board &amp; team
          </h2>
          <p className="leading-relaxed mb-10 max-w-3xl" style={{ color: "var(--ser-text-muted)" }}>
            SER Africa is guided by a dedicated board of directors and supported by a growing team of
            coordinators across the continent.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BOARD.map((m) => (
              <article
                key={m.id}
                className="ser-card overflow-hidden flex flex-col"
                data-testid={`board-card-${m.id}`}
              >
                <div className="aspect-[4/5] overflow-hidden" style={{ backgroundColor: "var(--ser-sand)" }}>
                  <img
                    src={m.photo}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    data-testid={`board-photo-${m.id}`}
                  />
                </div>
                <div className="p-5 flex flex-col grow">
                  <p
                    className="text-[11px] font-bold uppercase tracking-wider mb-2"
                    style={{ color: "var(--ser-clay)" }}
                  >
                    {m.role}{m.term ? ` · ${m.term}` : ""}
                  </p>
                  <h3
                    className="font-serif-display text-lg leading-tight mb-1"
                    style={{ color: "var(--ser-primary-dark)" }}
                  >
                    {m.name}
                  </h3>
                  <p className="text-xs mb-3" style={{ color: "var(--ser-text-muted)" }}>
                    {m.affiliation}
                  </p>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
                    {m.tagline}
                  </p>
                  <button
                    onClick={() => setOpenBio(m)}
                    className="mt-auto text-xs font-bold uppercase tracking-wider self-start"
                    style={{ color: "var(--ser-primary)" }}
                    data-testid={`board-read-bio-${m.id}`}
                  >
                    Read full bio →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {openBio && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={() => setOpenBio(null)}
          data-testid="board-bio-modal"
        >
          <div
            className="ser-card max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 p-6 border-b" style={{ borderColor: "var(--ser-border, #e5e5e5)" }}>
              <img
                src={openBio.photo}
                alt={openBio.name}
                className="w-20 h-20 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <p
                  className="text-[11px] font-bold uppercase tracking-wider mb-1"
                  style={{ color: "var(--ser-clay)" }}
                >
                  {openBio.role}{openBio.term ? ` · ${openBio.term}` : ""}
                </p>
                <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ser-primary-dark)" }}>
                  {openBio.name}
                </h3>
                <p className="text-xs mt-1" style={{ color: "var(--ser-text-muted)" }}>
                  {openBio.affiliation}
                </p>
              </div>
              <button
                onClick={() => setOpenBio(null)}
                className="p-1 rounded hover:bg-black/5"
                aria-label="Close bio"
                data-testid="board-bio-close"
              >
                <X className="w-5 h-5" style={{ color: "var(--ser-text-muted)" }} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {openBio.bio.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-primary-dark)", color: "white" }}
        data-testid="about-impact"
      >
        <div className="ser-container max-w-4xl text-center">
          <p className="eyebrow mb-3" style={{ color: "var(--ser-clay)" }}>Our Impact</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mb-12">Across the continent.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {[
              ["15+", "Countries"],
              ["500+", "Members"],
              ["30+", "Events Hosted"],
              ["50+", "Restoration Projects"],
            ].map(([stat, label]) => (
              <div key={label}>
                <p className="font-serif-display text-5xl mb-2" style={{ color: "var(--ser-clay)" }}>{stat}</p>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{label}</p>
              </div>
            ))}
          </div>
          <Link to="/membership" className="btn-ser-accent" data-testid="about-join-btn">
            Join our network
          </Link>
        </div>
      </section>
    </div>
  );
}
